/**
 * Notification Service
 * ============================================================================
 *
 * Purpose:
 * --------
 * Core notification service using functional programming principles.
 * Provides centralized notification management with queue, history, and preferences.
 *
 * This service provides:
 * - Create, update, and dismiss notifications
 * - Notification queue management
 * - Notification history tracking
 * - User preferences management
 * - Confirmation dialog management
 * - Duplicate prevention
 * - i18n integration
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** (no classes!)
 * - Pure functions where possible
 * - Immutable state updates
 * - Closure for state management
 * - Type-safe operations
 *
 * Usage:
 * ------
 * ```typescript
 * import { showNotification, dismissNotification } from 'core/notifications';
 *
 * const notification = showNotification('success', 'Operation successful!');
 * dismissNotification(notification.id);
 * ```
 * ============================================================================
 */

import type {
  ConfirmationDialog,
  ConfirmationOptions,
  Notification,
  NotificationHistoryItem,
  NotificationOptions,
  NotificationPreferences,
  NotificationQueueItem,
  NotificationServiceState,
  NotificationType,
} from './types';
import {
  DEFAULT_DURATION_BY_TYPE,
  DEFAULT_PRIORITY_BY_TYPE,
  NOTIFICATION_CONFIG,
  NOTIFICATION_I18N_KEYS,
  NOTIFICATION_POSITION,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_STORAGE_KEYS,
  NOTIFICATION_TYPE,
} from './constants';

import i18n from 'locales';

/**
 * Service State
 *
 * Using closure for state management (functional approach, not class).
 */
const state: NotificationServiceState = {
  notifications: [],
  queue: [],
  history: [],
  preferences: loadPreferences(),
  confirmation: null,
};

/**
 * State Change Listeners
 *
 * Subscribers to state changes.
 */
type StateChangeListener = (state: NotificationServiceState) => void;
const listeners: Set<StateChangeListener> = new Set();

/**
 * Subscribe to state changes
 *
 * Returns unsubscribe function.
 */
export const subscribeToNotifications = (listener: StateChangeListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Notify all listeners of state change
 */
const notifyListeners = (): void => {
  listeners.forEach((listener) => listener(state));
};

/**
 * Load preferences from localStorage
 */
function loadPreferences(): NotificationPreferences {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEYS.PREFERENCES);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load notification preferences:', error);
  }

  return {
    enabled: true,
    enableSounds: NOTIFICATION_CONFIG.ENABLE_SOUNDS,
    enableVibration: NOTIFICATION_CONFIG.ENABLE_VIBRATION,
    position: NOTIFICATION_CONFIG.DEFAULT_POSITION,
    showCloseButton: NOTIFICATION_CONFIG.SHOW_CLOSE_BUTTON,
    showProgressBar: NOTIFICATION_CONFIG.SHOW_PROGRESS_BAR,
    pauseOnHover: NOTIFICATION_CONFIG.PAUSE_ON_HOVER,
    dismissOnClick: NOTIFICATION_CONFIG.DISMISS_ON_CLICK,
  };
}

/**
 * Save preferences to localStorage
 */
const savePreferences = (): void => {
  try {
    localStorage.setItem(
      NOTIFICATION_STORAGE_KEYS.PREFERENCES,
      JSON.stringify(state.preferences)
    );
  } catch (error) {
    console.warn('Failed to save notification preferences:', error);
  }
};

/**
 * Load history from localStorage
 */
const loadHistory = (): NotificationHistoryItem[] => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEYS.HISTORY);
    if (stored) {
      const history = JSON.parse(stored);
      return history.map((item: NotificationHistoryItem) => ({
        ...item,
        createdAt: new Date(item.createdAt),
        dismissedAt: item.dismissedAt ? new Date(item.dismissedAt) : undefined,
      }));
    }
  } catch (error) {
    console.warn('Failed to load notification history:', error);
  }
  return [];
};

/**
 * Save history to localStorage
 */
const saveHistory = (): void => {
  try {
    localStorage.setItem(
      NOTIFICATION_STORAGE_KEYS.HISTORY,
      JSON.stringify(state.history.slice(0, NOTIFICATION_CONFIG.MAX_HISTORY_SIZE))
    );
  } catch (error) {
    console.warn('Failed to save notification history:', error);
  }
};

/**
 * Generate unique notification ID
 */
const generateId = (): string => {
  return `notification_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Get translated message
 *
 * Returns translated message if translationKey is provided, otherwise returns message as-is.
 */
export const getTranslatedMessage = (
  message: string,
  translationKey?: string,
  translationParams?: Record<string, string | number>
): string => {
  if (translationKey) {
    const translated = i18n.t(translationKey, translationParams || {});
    if (translated !== translationKey) {
      return translated;
    }
  }
  return message;
};

/**
 * Check if notification is duplicate
 *
 * Prevents showing duplicate notifications within a time window.
 */
const isDuplicate = (message: string, type: NotificationType): boolean => {
  const now = Date.now();
  const recentNotifications = state.notifications.filter(
    (n) =>
      n.message === message &&
      n.type === type &&
      now - n.createdAt.getTime() < NOTIFICATION_CONFIG.DUPLICATE_PREVENTION_WINDOW
  );
  return recentNotifications.length > 0;
};

/**
 * Create notification instance
 *
 * Pure function to create a notification object.
 */
export const createNotification = (
  type: NotificationType,
  message: string,
  options: NotificationOptions = {}
): Notification => {
  const translatedMessage = getTranslatedMessage(
    message,
    options.translationKey,
    options.translationParams
  );

  const translatedTitle = options.title
    ? getTranslatedMessage(
        options.title,
        options.translationKey ? `${options.translationKey}_title` : undefined,
        options.translationParams
      )
    : undefined;

  return {
    id: options.id || generateId(),
    type,
    message: translatedMessage,
    title: translatedTitle,
    description: options.description,
    duration: options.duration ?? DEFAULT_DURATION_BY_TYPE[type],
    position: options.position ?? state.preferences.position,
    priority: options.priority ?? DEFAULT_PRIORITY_BY_TYPE[type],
    icon: options.icon,
    actions: options.actions,
    dismissible: options.dismissible ?? true,
    showCloseButton: options.showCloseButton ?? state.preferences.showCloseButton,
    showProgressBar: options.showProgressBar ?? state.preferences.showProgressBar,
    pauseOnHover: options.pauseOnHover ?? state.preferences.pauseOnHover,
    translationKey: options.translationKey,
    translationParams: options.translationParams,
    className: options.className,
    style: options.style,
    metadata: options.metadata,
    createdAt: new Date(),
    source: options.source,
  };
};

/**
 * Add notification to history
 */
const addToHistory = (notification: Notification): void => {
  const historyItem: NotificationHistoryItem = {
    id: notification.id,
    type: notification.type,
    message: notification.message,
    title: notification.title,
    createdAt: notification.createdAt,
    dismissedAt: notification.dismissedAt,
    clicked: notification.clicked,
    source: notification.source,
  };

  state.history.unshift(historyItem);

  if (state.history.length > NOTIFICATION_CONFIG.MAX_HISTORY_SIZE) {
    state.history = state.history.slice(0, NOTIFICATION_CONFIG.MAX_HISTORY_SIZE);
  }

  saveHistory();
};

/**
 * Add notification to queue
 */
const addToQueue = (notification: Notification): void => {
  const queueItem: NotificationQueueItem = {
    notification,
    queuedAt: new Date(),
    priority: notification.priority ?? NOTIFICATION_PRIORITY.MEDIUM,
  };

  state.queue.push(queueItem);

  // Sort queue by priority (highest first)
  state.queue.sort((a, b) => b.priority - a.priority);

  // Trim queue if too large
  if (state.queue.length > NOTIFICATION_CONFIG.MAX_QUEUE_SIZE) {
    state.queue = state.queue.slice(0, NOTIFICATION_CONFIG.MAX_QUEUE_SIZE);
  }

  processQueue();
};

/**
 * Process notification queue
 *
 * Show notifications from queue if space available.
 */
const processQueue = (): void => {
  while (
    state.notifications.length < NOTIFICATION_CONFIG.MAX_VISIBLE &&
    state.queue.length > 0
  ) {
    const queueItem = state.queue.shift();
    if (queueItem) {
      state.notifications.push(queueItem.notification);
      notifyListeners();

      // Auto-dismiss if duration is set
      if (queueItem.notification.duration && queueItem.notification.duration > 0) {
        setTimeout(() => {
          dismissNotification(queueItem.notification.id);
        }, queueItem.notification.duration);
      }
    }
  }
};

/**
 * Show notification
 *
 * Main function to display a notification.
 */
export const showNotification = (
  type: NotificationType,
  message: string,
  options: NotificationOptions = {}
): Notification => {
  // Check if notifications are disabled
  if (!state.preferences.enabled) {
    console.log('Notifications are disabled');
    return createNotification(type, message, options);
  }

  // Check for duplicates
  if (options.preventDuplicate !== false && isDuplicate(message, type)) {
    console.log('Duplicate notification prevented:', message);
    // Return existing notification
    const existing = state.notifications.find((n) => n.message === message && n.type === type);
    return existing || createNotification(type, message, options);
  }

  const notification = createNotification(type, message, options);

  // Add to history
  addToHistory(notification);

  // Check if we can show immediately
  if (state.notifications.length < NOTIFICATION_CONFIG.MAX_VISIBLE) {
    state.notifications.push(notification);
    notifyListeners();

    // Auto-dismiss if duration is set
    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        dismissNotification(notification.id);
      }, notification.duration);
    }
  } else {
    // Add to queue
    addToQueue(notification);
  }

  return notification;
};

/**
 * Update notification
 *
 * Update an existing notification by ID.
 */
export const updateNotification = (
  id: string,
  updates: Partial<Notification>
): Notification | null => {
  const index = state.notifications.findIndex((n) => n.id === id);

  if (index === -1) {
    console.warn(`Notification not found: ${id}`);
    return null;
  }

  state.notifications[index] = {
    ...state.notifications[index],
    ...updates,
  };

  notifyListeners();
  return state.notifications[index];
};

/**
 * Dismiss notification
 *
 * Remove notification from display.
 */
export const dismissNotification = (id: string): void => {
  const index = state.notifications.findIndex((n) => n.id === id);

  if (index === -1) {
    return;
  }

  const notification = state.notifications[index];

  // Update history
  const historyIndex = state.history.findIndex((h) => h.id === id);
  if (historyIndex !== -1) {
    state.history[historyIndex].dismissedAt = new Date();
    saveHistory();
  }

  // Call onDismiss callback if provided
  if (notification.metadata?.onDismiss) {
    (notification.metadata.onDismiss as (notification: Notification) => void)(notification);
  }

  // Remove notification
  state.notifications.splice(index, 1);
  notifyListeners();

  // Process queue
  processQueue();
};

/**
 * Dismiss all notifications
 */
export const dismissAll = (): void => {
  state.notifications.forEach((notification) => {
    const historyIndex = state.history.findIndex((h) => h.id === notification.id);
    if (historyIndex !== -1) {
      state.history[historyIndex].dismissedAt = new Date();
    }
  });

  state.notifications = [];
  state.queue = [];
  saveHistory();
  notifyListeners();
};

/**
 * Get all active notifications
 */
export const getNotifications = (): Notification[] => {
  return [...state.notifications];
};

/**
 * Get notification by ID
 */
export const getNotificationById = (id: string): Notification | null => {
  return state.notifications.find((n) => n.id === id) || null;
};

/**
 * Get notification history
 */
export const getHistory = (): NotificationHistoryItem[] => {
  return [...state.history];
};

/**
 * Clear notification history
 */
export const clearHistory = (): void => {
  state.history = [];
  saveHistory();
  notifyListeners();
};

/**
 * Get preferences
 */
export const getPreferences = (): NotificationPreferences => {
  return { ...state.preferences };
};

/**
 * Update preferences
 */
export const updatePreferences = (
  updates: Partial<NotificationPreferences>
): NotificationPreferences => {
  state.preferences = {
    ...state.preferences,
    ...updates,
  };
  savePreferences();
  notifyListeners();
  return state.preferences;
};

/**
 * Show confirmation dialog
 *
 * Returns a promise that resolves to true if confirmed, false if canceled.
 */
export const showConfirmation = (options: ConfirmationOptions): Promise<boolean> => {
  return new Promise((resolve) => {
    const dialog: ConfirmationDialog = {
      id: generateId(),
      isOpen: true,
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      confirmText: options.confirmText,
      cancelText: options.cancelText,
      confirmVariant: options.confirmVariant,
      showCancel: options.showCancel ?? true,
      size: options.size,
      icon: options.icon,
      closable: options.closable ?? true,
      titleTranslationKey: options.titleTranslationKey,
      messageTranslationKey: options.messageTranslationKey,
      translationParams: options.translationParams,
      className: options.className,
      createdAt: new Date(),
      onConfirm: async () => {
        if (options.onConfirm) {
          await options.onConfirm();
        }
        resolve(true);
        closeConfirmation();
      },
      onCancel: () => {
        if (options.onCancel) {
          options.onCancel();
        }
        resolve(false);
        closeConfirmation();
      },
    };

    state.confirmation = dialog;
    notifyListeners();
  });
};

/**
 * Close confirmation dialog
 */
export const closeConfirmation = (): void => {
  state.confirmation = null;
  notifyListeners();
};

/**
 * Get active confirmation dialog
 */
export const getConfirmation = (): ConfirmationDialog | null => {
  return state.confirmation;
};

/**
 * Get current state
 */
export const getState = (): NotificationServiceState => {
  return {
    notifications: [...state.notifications],
    queue: [...state.queue],
    history: [...state.history],
    preferences: { ...state.preferences },
    confirmation: state.confirmation,
  };
};

/**
 * Initialize notification service
 *
 * Load history and preferences from storage.
 */
export const initializeNotificationService = (): void => {
  state.history = loadHistory();
  state.preferences = loadPreferences();
  notifyListeners();
};

// Auto-initialize on import
initializeNotificationService();
